import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';

interface FormFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  label: string;
  error?: string;
  hint?: ReactNode;
  /**
   * 입력값 앞에 고정으로 붙는 기호(`@` 등). 값의 일부가 아니라 형태를 보여주는
   * 장식이라 onChange 로 흘러가지 않는다.
   * 입력칸 위에 겹쳐 그리므로 1~2자까지만 자리가 맞는다.
   */
  prefix?: ReactNode;
}

/**
 * 공용 입력 필드
 * - label / error / hint 렌더링
 * - aria-invalid / aria-describedby 로 접근성 보장
 * - 프로젝트의 tailwind 토큰(brand-500, danger 등) 사용
 */
export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(
  function FormField({ label, error, hint, prefix, id, name, ...rest }, ref) {
    const inputId = id ?? name;
    const errorId = error ? `${inputId}-error` : undefined;
    const hintId = hint ? `${inputId}-hint` : undefined;
    const describedBy =
      [errorId, hintId].filter(Boolean).join(' ') || undefined;

    return (
      <label htmlFor={inputId} className="block">
        <span className="text-sm text-adobe-muted dark:text-neutral-400">
          {label}
        </span>
        <div className="relative mt-1">
          {prefix && (
            // 스크린리더에는 label 과 hint 로 이미 형태가 전달된다 — 중복 낭독 방지.
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 left-0 flex select-none items-center pl-3 text-sm text-adobe-muted dark:text-neutral-400"
            >
              {prefix}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            name={name}
            aria-invalid={!!error}
            aria-describedby={describedBy}
            className={[
              'block w-full py-2 rounded border',
              prefix ? 'pl-8 pr-3' : 'px-3',
              'dark:bg-dark-card dark:text-neutral-100 dark:placeholder:text-neutral-500',
              error
                ? 'border-danger focus-visible:ring-danger'
                : 'border-gray-300 focus-visible:ring-brand-500 dark:border-white/15',
              'focus-visible:ring-2 focus-visible:outline-none',
              'disabled:bg-gray-100 disabled:cursor-not-allowed dark:disabled:bg-white/10',
            ].join(' ')}
            {...rest}
          />
        </div>
        {hint && !error && (
          <span
            id={hintId}
            className="mt-1 block text-xs text-adobe-muted dark:text-neutral-400"
          >
            {hint}
          </span>
        )}
        {error && (
          <span
            id={errorId}
            role="alert"
            className="mt-1 block text-xs text-danger"
          >
            {error}
          </span>
        )}
      </label>
    );
  },
);
